import { type } from "arktype";
import { LoadNotation } from "./load";
import { Derived, Supervision } from "./layers";
import { BlockKind, DeviationReason, WorkKind } from "./vocabulary";

export const SetEntry = type({
  id: "string",
  "reps?": "number | null",
  "timeSeconds?": "number | null",
  "distanceM?": "number | null",
  "load?": LoadNotation,
  "rpe?": "number | null",
  "notes?": "string",
  workKind: WorkKind,
  "painReported?": "boolean",
});
export type SetEntry = typeof SetEntry.infer;

export const ExerciseEntry = type({
  id: "string",
  exerciseId: "string | null",
  exerciseText: "string",
  sets: SetEntry.array(),
  workKind: WorkKind,
  "skipped?": "boolean",
  "skipReason?": DeviationReason,
  "notes?": "string",
  "sensations?": type({ text: "string", location: "string", kind: "'discomfort' | 'pain' | 'unclassified'" }).array(),
});
export type ExerciseEntry = typeof ExerciseEntry.infer;

export const Block = type({ id: "string", kind: BlockKind, entries: ExerciseEntry.array() });
export type Block = typeof Block.infer;

export const Deviation = type({
  id: "string",
  plannedExerciseId: "string | null",
  plannedText: "string",
  actualText: "string",
  reason: DeviationReason,
  "reasonText?": "string",
  material: "boolean",
});
export type Deviation = typeof Deviation.infer;

export const Session = type({
  id: "string",
  clientId: "string",
  "planId?": "string | null",
  supervision: Supervision,
  effectiveAt: "string",
  observedAt: "string",
  status: "'provisional' | 'approved' | 'acknowledged' | 'superseded'",
  blocks: Block.array(),
  "deviations?": Deviation.array(),
  "openQuestionIds?": "string[]",
  "rawCaptureId?": "string | null",
  "title?": "string",
  "shareable?": "boolean",
  "coachNote?": "string",
});
export type Session = typeof Session.infer;

// Capture pipeline (R-CAP-02): the model step's typed contract.
export const StructuredSession = type({
  blocks: type({
    kind: BlockKind,
    entries: type({
      exerciseText: "string",
      workKind: WorkKind,
      "skipped?": "boolean",
      "skipReason?": DeviationReason,
      sets: type({ "reps?": "number | null", "timeSeconds?": "number | null", "distanceM?": "number | null", "loadText?": "string | null", "rpe?": "number | null", "notes?": "string" }).array(),
      "notes?": "string",
      "sensations?": type({ text: "string", location: "string" }).array(),
    }).array(),
  }).array(),
  "painMentions?": "string[]",
  "teachingInferred?": "string[]",
  "instructionsInNotes?": "string[]",
});
export type StructuredSession = typeof StructuredSession.infer;

export const RawCapture = type({
  id: "string",
  clientId: "string",
  kind: "'text' | 'photo' | 'audio' | 'import'",
  text: "string",
  "blobKey?": "string | null",
  receivedAt: "string",
});
export type RawCapture = typeof RawCapture.infer;

// R-CAP-03: only what needs the coach's eyes.
export const CheckBeforeSave = type({
  captureId: "string",
  header: { questions: "number", toConfirm: "number" },
  dictated: "string",
  ambiguities: type({ questionId: "string", text: "string", options: "string[]", entryId: "string" }).array(),
  sensations: type({ entryId: "string", text: "string", location: "string" }).array(),
  inferences: type({ id: "string", text: "string", layer: "'interpretation'", entryId: "string" }).array(),
  facts: type({ entryId: "string", label: "string", summary: "string" }).array(),
  draft: Session,
});
export type CheckBeforeSave = typeof CheckBeforeSave.infer;

export const SessionDerived = Derived;
